import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { createUseStyles } from 'react-jss';

import { AddressUtils } from '@bingads-webui-clientcenter/address';
import { Select } from '@bingads-webui-react/primitive-with-signal';
import { VirtualizedComboBox, ChoiceGroup as RadioGroup } from '@bingads-webui-react/fabric-control';
import { LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { useJsDataCallback, useJsDataContext } from '@bingads-webui-cc-react/js-data-rest';

import { AddressSelectorOptions } from '../constants';
import { getSelectedABL, formatAddressOptions } from '../utils';
import { getBusinessAddressSelectorStyles } from '../styles';

const { selectExistingAddressId, enterNewAddressId } = AddressSelectorOptions;
const { findDuplicateAddresses } = AddressUtils;

const useStyles = createUseStyles(getBusinessAddressSelectorStyles());

export const BusinessAddressSelector = ({
  i18n,
  mtPrefillAddressId,
  prefillAddressId,
  addressList,
  countryCode,
  handlePrefillAddressChange,
  label,
  helpPopupAriaLabel,
  isSignupFlow,
  isOptional,
  disabled,
  isEnhancedABLP1Enabled,
  disableAddressDelete,
  showRadioGroupInBusinessAddressSelector,
  onlyIncludeBusinessAIVAddresses,
  showEmptyBusinessAIVAddresses,
}) => {
  const classes = useStyles(getBusinessAddressSelectorStyles);
  const jsDataStore = useJsDataContext();
  const [inputValue, setInputValue] = useState(undefined);
  const [selectedAddressOptionKey, setSelectedAddressOptionKey] = useState(prefillAddressId === enterNewAddressId ? enterNewAddressId : selectExistingAddressId);
  const [addressIdToDelete, setAddressIdToDelete] = useState(null);
  const [deletedAddressIds, setDeletedAddressIds] = useState([]);
  const { execute: deleteCustomerAddress, data: deletedAddressId } = useJsDataCallback(jsData => jsData.CustomerAddress.destroy(addressIdToDelete, { params: { deleteSameAddresses: true } }), [addressIdToDelete]);
  const comboBoxRef = useRef(null);
  const onPrefillAddressChange = useCallback(({ addressId }) => {
    const {
      selectedAddress,
      selectedBusinessName,
      isSelectedAddressValid,
      isSelectedBusinessNameValid,
    } = getSelectedABL({
      addressId,
      addressList,
      countryCode,
      isOptional,
    });

    const selectedBusinessNameData = {
      businessName: selectedBusinessName,
      isValid: isSelectedBusinessNameValid,
    };

    const selectedAddressData = {
      address: selectedAddress,
      addressWithValidation: null,
      isValid: isSelectedAddressValid,
    };

    handlePrefillAddressChange({
      addressId,
      selectedAddressData,
      selectedBusinessNameData,
    });
  }, [addressList, countryCode, handlePrefillAddressChange, isOptional]);

  const formattedAddressOptions = useMemo(() => formatAddressOptions({
    addressList,
    currentCountry: countryCode,
    includedAddressIds: [prefillAddressId, mtPrefillAddressId],
  }), [addressList, countryCode, prefillAddressId, mtPrefillAddressId]);

  const selectExistingOptionDisabled = _.isEmpty(formattedAddressOptions.filter(({ key }) => !deletedAddressIds.includes(key))) || disabled;
  const comboboxSelectorDisabled = selectExistingOptionDisabled || (showRadioGroupInBusinessAddressSelector && prefillAddressId === enterNewAddressId);

  const addressOptions = useMemo(() => {
    const filteredAddressOptions = formattedAddressOptions
      .filter(({ key }) => !deletedAddressIds.includes(key))
      .filter(({ text }) => !inputValue || text.toLowerCase().includes(inputValue.toLowerCase()));

    return [
      ...(showRadioGroupInBusinessAddressSelector ? [] : [{ key: enterNewAddressId, text: i18n.getString(_TL_('Enter a new address...')) }]),
      ...filteredAddressOptions,
    ];
  }, [deletedAddressIds, formattedAddressOptions, i18n, inputValue, showRadioGroupInBusinessAddressSelector]);

  const selectedKey = useMemo(
    () => {
      if (prefillAddressId === enterNewAddressId && !showRadioGroupInBusinessAddressSelector) {
        return enterNewAddressId;
      }
      return _.find(formattedAddressOptions, ({ key }) => key === prefillAddressId) ? prefillAddressId : null;
    },
    [formattedAddressOptions, prefillAddressId, showRadioGroupInBusinessAddressSelector]
  );

  const helpPopupTopic = useMemo(() => {
    if (isSignupFlow) {
      return 'Pop_BA_Signup_BusinessAddress';
    }
    if (onlyIncludeBusinessAIVAddresses) {
      return 'pop_BA_AIV_BusinessInfo';
    }
    return isEnhancedABLP1Enabled ? 'pop_BA_Accounts_BusinessAddressRemove' : 'Pop_BA_Accounts_BusinessAddress';
  }, [isEnhancedABLP1Enabled, isSignupFlow, onlyIncludeBusinessAIVAddresses]);

  const handleAddressDelete = useCallback((event, key) => {
    event.stopPropagation();
    setAddressIdToDelete(key);
    setDeletedAddressIds(prev => [...prev, key]);

    if (key === prefillAddressId) {
      setSelectedAddressOptionKey(selectExistingAddressId);
      onPrefillAddressChange({ addressId: selectExistingAddressId });
    }
  }, [onPrefillAddressChange, prefillAddressId]);

  useEffect(() => {
    if (addressIdToDelete) {
      deleteCustomerAddress();
    }
  }, [addressIdToDelete, deleteCustomerAddress]);

  useEffect(() => {
    if (deletedAddressId) {
      const duplicatedAddresses = findDuplicateAddresses(addressList, { targetAddressId: deletedAddressId });
      _.forEach(duplicatedAddresses, ({ Id }) => jsDataStore.CustomerAddress.eject(Id));
    }
  }, [addressList, deletedAddressId, jsDataStore.CustomerAddress]);

  useEffect(() => {
    if (selectExistingOptionDisabled && selectedAddressOptionKey === selectExistingAddressId) {
      setSelectedAddressOptionKey(enterNewAddressId);
      onPrefillAddressChange({ addressId: enterNewAddressId });
    }
  }, [selectExistingOptionDisabled, selectedAddressOptionKey, onPrefillAddressChange]);

  // The address selector radio group has to be a controlled component to handle country switch or address validation scenarios
  // In those scenarios the selected address option should be updated programmatically
  useEffect(() => {
    if (Object.values(AddressSelectorOptions).includes(prefillAddressId)) {
      setSelectedAddressOptionKey(prefillAddressId);
    } else {
      setSelectedAddressOptionKey(selectExistingAddressId);
    }
  }, [prefillAddressId]);

  const renderComboAddressSelector = useCallback((styles = {}) => (
    <VirtualizedComboBox
      className="business-address-selector-combobox"
      componentRef={comboBoxRef}
      selectedKey={selectedKey}
      onChange={(_event, option) => {
        if (option && _.isNumber(option.key)) {
          onPrefillAddressChange({ addressId: option.key });
        }
        comboBoxRef.current.dismissMenu();
        setInputValue(undefined);
      }}
      onPendingValueChanged={(option, _index, value) => {
        if (_.isString(value)) {
          setInputValue(value);
        }
        if (option || _.isString(value)) {
          comboBoxRef.current.focus(true);
        }
      }}
      autofill={{
        onFocus: () => !comboboxSelectorDisabled && comboBoxRef.current.focus(true),
      }}
      onBlur={() => setInputValue(undefined)}
      allowFreeform
      autoComplete="on"
      placeholder={i18n.getString(_TL_('Please select or fill in to search'))}
      options={addressOptions}
      useComboBoxAsMenuWidth
      disabled={comboboxSelectorDisabled}
      ariaLabel={i18n.getString(_TL_('Business address'))}
      styles={{ rootFocused: { '&::after': { borderWidth: '1px' } }, ...styles }}
      comboBoxOptionStyles={{ optionTextWrapper: { width: '100%' } }}
      onRenderOption={({ key, text }) => {
        const disableDelete = key === mtPrefillAddressId || disableAddressDelete;
        return (
          <div
            key={key}
            data-value={key}
            className={classnames('business-address-selector-option', classes.selectorOption)}
          >
            {text}
            {key !== enterNewAddressId &&
              <span
                role="button"
                className={classnames('delete-address-button', classes.deleteAddressBtn, { [classes.deleteDisabled]: disableDelete })}
                onClick={event => !disableDelete && handleAddressDelete(event, key)}
                tabIndex={0}
                title={i18n.getString(_TL_('Remove from Business address list'))}
                onKeyDown={_.noop}
              >
                {i18n.getString(_TL_('Remove'))}
              </span>
            }
          </div>);
        }}
    />
  ), [addressOptions, classes, comboboxSelectorDisabled, disableAddressDelete, handleAddressDelete, i18n, mtPrefillAddressId, onPrefillAddressChange, selectedKey]);

  const addressSelectorOptions = useMemo(() => [
    { key: selectExistingAddressId, text: i18n.getString(_TL_('Select an existing address')), disabled: selectExistingOptionDisabled },
    {
      key: 'combobox-selector',
      disabled: true,
      onRenderField: () => (
        renderComboAddressSelector({ container: { marginLeft: 25 } })
      ),
    },
    {
      key: enterNewAddressId,
      text: i18n.getString(_TL_('Create a new address')),
    },
  ], [i18n, renderComboAddressSelector, selectExistingOptionDisabled]);

  const handleAddressSelectorOptionChange = useCallback((_event, option) => {
    setSelectedAddressOptionKey(option.key);
    onPrefillAddressChange({ addressId: option.key });
  }, [onPrefillAddressChange]);

  return (
    <LineByLineFormGroup
      contentColClass={classnames('col-xs-6', 'prefill-business-address-control')}
      label={label || i18n.getString(_TL_('Business address'))}
      helpPopupTopic={helpPopupTopic}
      helpPopupAriaLabel={helpPopupAriaLabel}
      id="business-address-selector"
      className="business-address-selector"
    >
      {isEnhancedABLP1Enabled && showRadioGroupInBusinessAddressSelector &&
      <RadioGroup
        ariaLabelledBy="business-address-selector-label"
        className={classes.addressSelectorRadioGroup}
        selectedKey={selectedAddressOptionKey}
        options={addressSelectorOptions}
        onChange={handleAddressSelectorOptionChange}
      />}
      {isEnhancedABLP1Enabled && !showRadioGroupInBusinessAddressSelector && renderComboAddressSelector()}
      {!isEnhancedABLP1Enabled &&
        <Select
          value={prefillAddressId}
          onChange={({ target: { value: addressId } }) => onPrefillAddressChange({ addressId: Number(addressId) })}
          disabled={disabled}
          aria-label={i18n.getString(_TL_('Business address'))}
        >
          <option key="enter-new" value={enterNewAddressId.toString()} hidden={onlyIncludeBusinessAIVAddresses && !showEmptyBusinessAIVAddresses} >
            {onlyIncludeBusinessAIVAddresses
              ? i18n.getString(_TL_('Select a verified company name and address'))
              : i18n.getString(_TL_('Enter a new address...'))}
          </option>
          {formattedAddressOptions.map(({ key, text }) => <option value={key} key={key}>{text}</option>)}
        </Select>}
      {showEmptyBusinessAIVAddresses &&
        <div className={classnames('alert', 'alert-warning', classes.emptyBusinessAIVAddressWarning)} role="alert">
          <span className="alert-image" />
          <span role="alert">
            {i18n.getString(_TL_('No verified business information is available. Add a business name and address in Account settings.'))}
          </span>
        </div>}
    </LineByLineFormGroup>
  );
};

BusinessAddressSelector.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  prefillAddressId: PropTypes.number,
  mtPrefillAddressId: PropTypes.number,
  addressList: PropTypes.arrayOf(PropTypes.any).isRequired,
  countryCode: PropTypes.string.isRequired,
  handlePrefillAddressChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  helpPopupAriaLabel: PropTypes.string,
  isSignupFlow: PropTypes.bool,
  isOptional: PropTypes.bool,
  disabled: PropTypes.bool,
  isEnhancedABLP1Enabled: PropTypes.bool,
  disableAddressDelete: PropTypes.bool,
  showRadioGroupInBusinessAddressSelector: PropTypes.bool,
  onlyIncludeBusinessAIVAddresses: PropTypes.bool,
  showEmptyBusinessAIVAddresses: PropTypes.bool,
};

BusinessAddressSelector.defaultProps = {
  prefillAddressId: enterNewAddressId,
  mtPrefillAddressId: null,
  label: '',
  helpPopupAriaLabel: '',
  isSignupFlow: false,
  isOptional: false,
  disabled: false,
  isEnhancedABLP1Enabled: false,
  disableAddressDelete: false,
  showRadioGroupInBusinessAddressSelector: false,
  onlyIncludeBusinessAIVAddresses: false,
  showEmptyBusinessAIVAddresses: false,
};
